import type { ProjectBreakdownPoint } from '@ace-tracker/shared';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';

/** A projectId that is still the collector's salted hash rather than a readable name. */
const HASHED_PROJECT_ID = /^[0-9a-f]{16}$/;

/**
 * Per-project usage, Claude Code only. `projectId` is the readable project
 * name (repository `owner/repo`, or a folder name) when the collector opts in
 * with `sanitization.sendRawProjectId`; otherwise it is a salted one-way hash
 * of the local project path with no reverse lookup (see
 * collector/docs/PRIVACY.md). Hashed rows are labelled as such so they are
 * never mistaken for real names.
 */
export function ProjectBreakdownTable({ byProject, info }: { byProject: ProjectBreakdownPoint[]; info?: string }) {
  const sorted = [...byProject].sort((a, b) => b.events - a.events);
  const anyHashed = sorted.some((p) => HASHED_PROJECT_ID.test(p.projectId));

  return (
    <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <ExportMenu title="Usage by project" className="float-right -mr-2 -mt-2 ml-2" />
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
        Usage by project
        {info && <InfoTooltip text={info} />}
      </h3>
      <p className="mt-1 text-xs text-text-muted">
        Each row is one project, named by its Git repository (or folder name outside a repository).
        {anyHashed && (
          <>
            {' '}
            Rows marked <span className="font-medium">hashed</span> come from collectors without{' '}
            <code className="font-mono text-xs">sanitization.sendRawProjectId</code> and can't be mapped back to a name.
          </>
        )}
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Project</th>
              <th className="pb-2.5 pr-3 text-right">Events</th>
              <th className="pb-2.5 pr-3 text-right">Input tokens</th>
              <th className="pb-2.5 pr-3 text-right">Output tokens</th>
              <th className="pb-2.5 text-right">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const hashed = HASHED_PROJECT_ID.test(p.projectId);
              return (
                <tr key={p.projectId} className="border-b border-border last:border-0">
                  <td className="max-w-[360px] break-all py-2.5 pr-3 font-mono text-xs text-text-default">
                    {p.projectId}
                    {hashed && <span className="ml-2 font-sans text-[11px] text-text-faint">(hashed)</span>}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{p.events}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{p.inputTokens.toLocaleString()}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{p.outputTokens.toLocaleString()}</td>
                  <td className="py-2.5 text-right tabular-nums text-text-muted">{new Date(p.lastSeen).toLocaleString()}</td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td className="py-3 text-text-muted" colSpan={5}>
                  No project usage in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
